import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AppApis } from '@app/core/APIs/app-apis';
import { map } from 'rxjs';
import type { BusinessCalendarDefinition } from '../../business-calendars/platform-business-calendars.models';
import type {
  RecurringScheduleCatalog,
  RecurringScheduleControl,
  RecurringScheduleDefinition,
  RecurringScheduleRun,
  RecurringScheduleVersion,
  ScheduleControlCommand,
  ScheduleRecoveryAction,
} from './recurring-schedules.models';
const options = {
  withCredentials: true,
  headers: new HttpHeaders({
    'skip-error-interceptor': 'yes',
    'skip-loading-interceptor': 'yes',
  }),
};
const write = (key: string, eTag?: string) => ({
  ...options,
  headers: eTag
    ? options.headers.set('Idempotency-Key', key).set('If-Match', eTag)
    : options.headers.set('Idempotency-Key', key),
});
@Injectable({ providedIn: 'root' })
export class RecurringSchedulesService {
  private readonly http = inject(HttpClient);
  private readonly apis = inject(AppApis);
  private get url(): string {
    return this.apis.core.manual.getPlatformRecurringSchedules();
  }
  catalog() {
    return this.http.get<ScheduleCatalogWire>(this.url, options).pipe(
      map((catalog) => ({
        ...catalog,
        calendars: catalog.calendars.map((calendar) => ({
          ...calendar.definition,
          contentHash: calendar.contentHash,
          managed: false,
        })),
      })),
    );
  }
  create(label: string, key: string, code?: string) {
    return this.http.post<RecurringScheduleVersion>(
      `${this.url}/drafts`,
      { label, ...(code ? { code } : {}) },
      write(key),
    );
  }
  save(
    source: RecurringScheduleVersion,
    definition: RecurringScheduleDefinition,
    key: string,
  ) {
    return this.http.put<RecurringScheduleVersion>(
      `${this.url}/drafts/${source.id}`,
      { expectedRevision: source.revision, definition },
      write(key, source.eTag),
    );
  }
  publish(source: RecurringScheduleVersion, key: string) {
    return this.http.post<RecurringScheduleVersion>(
      `${this.url}/drafts/${source.id}/publish`,
      { expectedRevision: source.revision },
      write(key, source.eTag),
    );
  }
  control(
    source: RecurringScheduleControl,
    command: ScheduleControlCommand,
    key: string,
  ) {
    return this.http.put<RecurringScheduleControl>(
      `${this.url}/${encodeURIComponent(source.code)}/control`,
      command,
      write(key, source.eTag),
    );
  }
  runs(code?: string) {
    return this.http.get<RecurringScheduleRun[]>(`${this.url}/runs`, {
      ...options,
      params: code
        ? new HttpParams().set('code', code).set('take', 50)
        : new HttpParams().set('take', 50),
    });
  }
  runNow(source: RecurringScheduleControl, reason: string, key: string) {
    return this.http.post<RecurringScheduleRun>(
      `${this.url}/${encodeURIComponent(source.code)}/run-now`,
      {
        expectedRevision: source.revision,
        publishedVersionId: source.publishedVersionId,
        reason,
      },
      write(key, source.eTag),
    );
  }
  recover(
    run: RecurringScheduleRun,
    action: ScheduleRecoveryAction,
    reason: string,
    key: string,
  ) {
    return this.http.post<RecurringScheduleRun>(
      `${this.url}/runs/${encodeURIComponent(run.id)}/recover`,
      { action, reason },
      write(key, run.eTag),
    );
  }
}
/** The generic server calendar version wraps its immutable definition and hash. */
type ScheduleCatalogWire = Omit<RecurringScheduleCatalog, 'calendars'> & {
  calendars: { definition: BusinessCalendarDefinition; contentHash: string }[];
};
